// DashboardUploadPage.jsx
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { UploadCloud, ChevronLeft, ChevronRight } from "lucide-react";
// Mocked user credentials: username => password (BJJ leg submissions)
const userCredentials = {
  Gert: "heel hook",
  Mehdi: "straight ankle lock",
};

/**
 * Simple login page for mocked users
 */
function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (userCredentials[username] && userCredentials[username] === password) {
      setError(null);
      onLogin(username);
    } else {
      setError("Invalid username or password");
    }
  };
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Card className="w-full max-w-sm">
        <CardContent className="p-8">
          <h2 className="text-2xl mb-4 font-semibold text-center">Login</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-1 block w-full border border-input rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full border border-input rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <Button type="submit" className="w-full">
            Login
          </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function DashboardUploadPage({ user }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [date, setDate] = useState(new Date());

  // Environment variables provided by Vite; must start with VITE_
  const API_URL = import.meta.env.VITE_API_URL;
  const USER_ID = user;

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      setUploadStatus(null);
    }
  };

  // Uploads the image: requests a presigned URL then PUTs the file to S3
  const handleUpload = async () => {
    if (!selectedImage) return;
    if (!API_URL || !USER_ID) {
      setUploadStatus('Error: Missing VITE_API_URL or VITE_USER_ID in environment');
      return;
    }
    setUploadStatus('Requesting upload URL...');
    try {
      const res = await fetch(`${API_URL}/upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: USER_ID,
          filename: selectedImage.name,
          datetime: date.toISOString(),
          content_type: selectedImage.type,
        }),
      });
      if (!res.ok) throw new Error(`Failed to get upload URL: ${res.status} ${res.statusText}`);
      const { upload_url: uploadUrl, upload_fields: uploadFields } = await res.json();
      setUploadStatus('Uploading to S3...');
      // Prepare multipart form data for presigned POST
      const formData = new FormData();
      Object.entries(uploadFields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', selectedImage);
      const uploadRes = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });
      if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.status} ${uploadRes.statusText}`);
      setUploadStatus('Upload successful 🎉');
    } catch (err) {
      console.error(err);
      setUploadStatus(`Error: ${err.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto grid gap-6">
        <h1 className="text-3xl font-bold text-center">📆 Food Tracker Dashboard</h1>

        <Card>
          <CardContent className="p-4 grid md:grid-cols-2 gap-4 items-center">
            <div>
              <label className="block mb-2 font-medium">Select an image of your meal</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="block w-full text-sm"
              />
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="mt-4 rounded-xl border shadow-md max-h-[16rem] max-w-[20rem] object-contain"
                />
              )}
            </div>
            <div className="flex flex-col items-center gap-4">
              <Calendar mode="single" selected={date} onDayClick={(d) => d && setDate(d)} className="rounded-md border" />
              <Button onClick={handleUpload} disabled={!selectedImage}>
                <UploadCloud className="mr-2 h-4 w-4" /> Upload to S3
              </Button>
              {uploadStatus && <p className="text-sm text-green-600">{uploadStatus}</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ImageCard({ image, user }) {
  const [analysisHistory, setAnalysisHistory] = useState(image.analysis_history || []);
  const [loading, setLoading] = useState(false);
  const API_URL = import.meta.env.VITE_API_URL;

  const latest = analysisHistory.length > 0 ? analysisHistory[0] : null;
  const [showHistory, setShowHistory] = useState(false);

  const handleAnalyze = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/process-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user, metadata_key: image.metadata_key })
      });
      if (!res.ok) throw new Error(`Analysis failed: ${res.status} ${res.statusText}`);
      const data = await res.json();
      setAnalysisHistory((prev) => [data.analysis, ...prev]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-shrink-0 bg-white p-2 rounded shadow-md w-[12rem] md:w-[16rem]">
      <img src={image.image_url} alt={image.filename} className="w-full h-[8rem] md:h-[10rem] object-cover rounded" />
      <div className="mt-2">
        <p className="text-sm font-medium">
          {new Date(image.datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
        {!latest && (
          <Button
            onClick={handleAnalyze}
            disabled={loading}
            className="mt-2 w-full"
          >
            {loading ? 'Analysing...' : 'Analyse'}
          </Button>
        )}
        {latest && (
          <>
            <div className="mt-2 w-full max-w-[16rem] max-h-24 overflow-y-auto text-sm bg-gray-100 p-2 rounded break-words">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {latest.analysis}
              </ReactMarkdown>
            </div>
            <Button
              onClick={handleAnalyze}
              disabled={loading}
              className="mt-2 w-full"
            >
              {loading ? 'Analysing...' : 'Analyse Again'}
            </Button>
            {analysisHistory.length > 1 && (
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="mt-2 text-sm text-primary"
              >
                {showHistory ? 'Hide History' : 'Show History'}
              </button>
            )}
            {showHistory && (
              <div className="mt-2 w-full max-w-[16rem] max-h-32 overflow-y-auto bg-gray-50 p-2 rounded text-xs break-words">
                {analysisHistory.slice(1).map((entry, idx) => (
                  <div key={idx} className="mb-2">
                    <p className="font-semibold text-xs mb-1">
                      {new Date(entry.timestamp).toLocaleString()}
                    </p>
                    {entry.prompt && (
                      <p className="italic text-xs mb-1">Prompt: {entry.prompt}</p>
                    )}
                    <div className="text-xs break-words">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {entry.analysis}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DayViewPage({ user }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  // offset in weeks for navigating the 7-day window
  const [weekOffset, setWeekOffset] = useState(0);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const API_URL = import.meta.env.VITE_API_URL;

  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      setError(null);
      try {
        const dateStr = selectedDate.toISOString().split('T')[0];
        const res = await fetch(`${API_URL}/images?user=${user}&date=${dateStr}`);
        if (!res.ok) throw new Error(`Failed to load images: ${res.status} ${res.statusText}`);
        const data = await res.json();
        setImages(data.images || []);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, [selectedDate, user]);

  // Generate a 7-day window based on weekOffset
  const today = new Date();
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    // shift by weekOffset * 7 days, then back by i days
    d.setDate(today.getDate() + weekOffset * 7 - i);
    days.push(d);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto">
        {/* Date selector with prev/next week */}
        <div className="sticky top-0 bg-white z-10 border-b">
          <div className="flex items-center justify-between p-4 space-x-2">
            <Button
              onClick={() => setWeekOffset(o => o - 1)}
              variant="outline"
              size="sm"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex overflow-x-auto space-x-2 flex-1 justify-center">
              {days.map((d) => {
                const isSelected = d.toDateString() === selectedDate.toDateString();
                return (
                  <Button
                    key={d.toISOString()}
                    onClick={() => setSelectedDate(d)}
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-full whitespace-nowrap px-4 py-2"
                  >
                    {d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}
                  </Button>
                );
              })}
            </div>
            <Button
              onClick={() => setWeekOffset(o => o + 1)}
              variant="outline"
              size="sm"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {/* Content */}
      <div className="p-4">
        {loading && <p className="text-center">Loading images...</p>}
        {error && <p className="text-center text-red-600">Error: {error}</p>}
        {!loading && images.length === 0 && <p className="text-center">No meals logged for this day.</p>}
        <div className="flex overflow-x-auto space-x-4 mt-4 pb-4">
          {images.map((img) => (
            <ImageCard key={img.metadata_key} image={img} user={user} />
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}
// Impact Report page for date-range analysis
function ReportPage({ user }) {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conclusion, setConclusion] = useState(null);
  const [history, setHistory] = useState([]);

  const API_URL = import.meta.env.VITE_API_URL;
  const USER_ID = user;

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/conclusions?user=${USER_ID}`);
      if (!res.ok) throw new Error(`History failed: ${res.status} ${res.statusText}`);
      const data = await res.json();
      setHistory(data.conclusions || []);
    } catch (err) {
      console.error('Fetch history error:', err);
    }
  };
  // Load history on mount and after report generation
  useEffect(() => {
    fetchHistory();
  }, [user]);
  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);
    setConclusion(null);
    try {
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];
      const res = await fetch(`${API_URL}/process-range`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: USER_ID, start_date: start, end_date: end })
      });
      if (!res.ok) throw new Error(`Report failed: ${res.status} ${res.statusText}`);
      const data = await res.json();
      setConclusion(data.conclusion);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
      // Refresh history after new conclusion
      fetchHistory();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto grid gap-6">
        <h1 className="text-3xl font-bold text-center">📊 Impact Report</h1>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h2 className="text-sm font-medium mb-2">Start Date</h2>
            <Calendar mode="single" selected={startDate} onDayClick={(d) => d && setStartDate(d)} className="rounded-md border" />
          </div>
          <div>
            <h2 className="text-sm font-medium mb-2">End Date</h2>
            <Calendar mode="single" selected={endDate} onDayClick={(d) => d && setEndDate(d)} className="rounded-md border" />
          </div>
        </div>
        <div className="text-center">
          <Button onClick={handleGenerateReport} disabled={loading}>Generate Report</Button>
        </div>
        {loading && <p className="text-center">Loading...</p>}
        {error && <p className="text-center text-red-600">Error: {error}</p>}
        {conclusion && (
          <div className="bg-white p-4 rounded shadow text-sm break-words max-w-prose mx-auto max-h-[60vh] overflow-y-auto">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {conclusion.conclusion}
            </ReactMarkdown>
          </div>
        )}
        {/* Impact report history */}
        <div className="mt-6 max-w-4xl mx-auto">
          <h2 className="text-xl font-semibold mb-2">Report History</h2>
          {history.length === 0 ? (
            <p className="text-sm text-center">No past reports.</p>
          ) : (
            <div className="space-y-4">
              {history.map((entry, idx) => (
                <details key={idx} className="bg-white p-4 rounded shadow">
                  <summary className="cursor-pointer font-medium">
                    {entry.start_date} → {entry.end_date} ({new Date(entry.timestamp).toLocaleString()})
                  </summary>
                  <div className="mt-2 text-sm whitespace-pre-wrap break-words">
                    {entry.prompt && (
                      <p className="italic text-xs mb-2">Prompt: {entry.prompt}</p>
                    )}
                    <div className="text-sm break-words">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {entry.conclusion}
                      </ReactMarkdown>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// Page to display current prompt templates from the backend
function PromptsPage({ user }) {
  const [prompts, setPrompts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageValue, setImageValue] = useState('');
  const [rangeValue, setRangeValue] = useState('');
  const [saveStatus, setSaveStatus] = useState(null);
  const API_URL = import.meta.env.VITE_API_URL;
  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/prompts`)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load prompts: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setPrompts(data);
        setImageValue(data.image_prompt);
        setRangeValue(data.range_prompt);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto grid gap-6">
        <h1 className="text-3xl font-bold text-center">⚙️ Prompts</h1>
        {loading && <p>Loading prompts...</p>}
        {error && <p className="text-red-600">Error: {error}</p>}
        {prompts && (
          <div className="bg-white p-4 rounded shadow text-sm space-y-4">
            <div>
              <h2 className="font-medium mb-1">Image Prompt</h2>
              <textarea
                value={imageValue}
                onChange={(e) => setImageValue(e.target.value)}
                rows={4}
                className="w-full p-2 bg-gray-100 rounded border"
              />
              <Button
                onClick={async () => {
                  setSaveStatus('Saving image prompt...');
                  try {
                    const res = await fetch(`${API_URL}/prompts`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        image_prompt: imageValue,
                        user
                      }),
                    });
                    if (!res.ok) throw new Error(`Save failed: ${res.status}`);
                    const data = await res.json();
                    setPrompts((prev) => ({
                      image_prompt: data.image_prompt,
                      range_prompt: data.range_prompt,
                      history: [data, ...(prev.history || [])]
                    }));
                    setSaveStatus('Image prompt saved');
                  } catch (err) {
                    setSaveStatus(err.message);
                  }
                }}
                className="mt-2"
              >
                Save Image Prompt
              </Button>
            </div>
            <div>
              <h2 className="font-medium mb-1">Range Prompt</h2>
              <textarea
                value={rangeValue}
                onChange={(e) => setRangeValue(e.target.value)}
                rows={4}
                className="w-full p-2 bg-gray-100 rounded border"
              />
            <Button
                onClick={async () => {
                  setSaveStatus('Saving range prompt...');
                  try {
                    const res = await fetch(`${API_URL}/prompts`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        range_prompt: rangeValue,
                        user
                      }),
                    });
                    if (!res.ok) throw new Error(`Save failed: ${res.status}`);
                    const data = await res.json();
                    setPrompts((prev) => ({
                      image_prompt: data.image_prompt,
                      range_prompt: data.range_prompt,
                      history: [data, ...(prev.history || [])]
                    }));
                    setSaveStatus('Range prompt saved');
                  } catch (err) {
                    setSaveStatus(err.message);
                  }
                }}
                className="mt-2"
              >
                Save Range Prompt
              </Button>
            </div>
            {saveStatus && <p className="text-center text-sm">{saveStatus}</p>}
        {prompts.history && prompts.history.length > 0 && (
          <div className="pt-4 border-t">
            <h3 className="font-medium">Prompt History</h3>
            {prompts.history.map((entry, idx) => (
              <details key={idx} className="mt-2 p-2 bg-gray-100 rounded">
                <summary className="cursor-pointer text-sm font-medium">
                  {new Date(entry.timestamp).toLocaleString()} by {entry.user}
                </summary>
                <div className="mt-2 space-y-2 text-sm max-h-48 overflow-y-auto">
                  <div>
                    <h4 className="font-medium">Image Prompt</h4>
                    <pre className="whitespace-pre-wrap bg-white p-2 rounded">
                      {entry.image_prompt}
                    </pre>
                  </div>
                  <div>
                    <h4 className="font-medium">Range Prompt</h4>
                    <pre className="whitespace-pre-wrap bg-white p-2 rounded">
                      {entry.range_prompt}
                    </pre>
                  </div>
                </div>
              </details>
            ))}
          </div>
        )}
          </div>
        )}
      </div>
    </div>
  );
}
// Page to display version info from frontend and backend
function VersionsPage() {
  const [frontVersion, setFrontVersion] = useState(null);
  const [backVersion, setBackVersion] = useState(null);
  const [error, setError] = useState(null);
  const API_URL = import.meta.env.VITE_API_URL;
  useEffect(() => {
    // Frontend version
    fetch('/version.xml')
      .then(res => res.text())
      .then(text => {
        const parser = new DOMParser();
        const xml = parser.parseFromString(text, 'application/xml');
        setFrontVersion({
          code: xml.querySelector('code_version')?.textContent,
          sha: xml.querySelector('git_sha')?.textContent,
          dt: xml.querySelector('deploy_datetime')?.textContent
        });
      })
      .catch(err => setError(`Frontend version load error: ${err.message}`));
    // Backend version
    if (API_URL) {
      fetch(`${API_URL}/version`)
        .then(res => res.text())
        .then(text => {
          const parser = new DOMParser();
          const xml = parser.parseFromString(text, 'application/xml');
          setBackVersion({
            code: xml.querySelector('code_version')?.textContent,
            sha: xml.querySelector('git_sha')?.textContent,
            dt: xml.querySelector('deploy_datetime')?.textContent
          });
        })
        .catch(err => setError(prev => prev ? prev + '; ' + err.message : `Backend version load error: ${err.message}`));
    }
  }, []);
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <h2 className="text-2xl font-bold mb-4 text-center">Version Information</h2>
      {error && <p className="text-red-600">{error}</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded shadow">
          <h3 className="font-semibold mb-2">Frontend</h3>
          {frontVersion ? (
            <ul className="list-none space-y-1">
              <li><strong>Code Version:</strong> {frontVersion.code}</li>
              <li><strong>Git SHA:</strong> {frontVersion.sha}</li>
              <li><strong>Deploy Datetime:</strong> {frontVersion.dt}</li>
            </ul>
          ) : <p>Loading...</p>}
        </div>
        <div className="bg-white p-4 rounded shadow">
          <h3 className="font-semibold mb-2">Backend</h3>
          {backVersion ? (
            <ul className="list-none space-y-1">
              <li><strong>Code Version:</strong> {backVersion.code}</li>
              <li><strong>Git SHA:</strong> {backVersion.sha}</li>
              <li><strong>Deploy Datetime:</strong> {backVersion.dt}</li>
            </ul>
          ) : <p>Loading...</p>}
        </div>
      </div>
    </div>
  );
}

export default function AppWrapper() {
  const [currentUser, setCurrentUser] = useState(() => localStorage.getItem('user') || null);
  const [view, setView] = useState('upload');

  const handleLogout = () => {
    localStorage.removeItem('user');
    setCurrentUser(null);
  };

  if (!currentUser) {
    return <LoginPage onLogin={(user) => { localStorage.setItem('user', user); setCurrentUser(user); }} />;
  }

  return (
    // full viewport width container to stick sidebar right
    <div className="flex min-h-screen w-screen">
      {/* Main content area */}
      <div className="flex-1">
        {view === 'upload' ? (
          <DashboardUploadPage user={currentUser} />
        ) : view === 'day' ? (
          <DayViewPage user={currentUser} />
        ) : view === 'report' ? (
          <ReportPage user={currentUser} />
        ) : view === 'prompts' ? (
          <PromptsPage user={currentUser} />
        ) : view === 'versions' ? (
          <VersionsPage />
        ) : null}
      </div>
      {/* Sidebar menu and user info */}
      <aside className="flex-shrink-0 w-48 bg-white border-l shadow-inner p-4 flex flex-col justify-between">
        <nav className="space-y-2">
          <Button
            variant={view === 'upload' ? 'default' : 'ghost'}
            className="w-full justify-start"
            onClick={() => setView('upload')}
          >Upload</Button>
          <Button
            variant={view === 'day' ? 'default' : 'ghost'}
            className="w-full justify-start"
            onClick={() => setView('day')}
          >Day View</Button>
          <Button
            variant={view === 'report' ? 'default' : 'ghost'}
            className="w-full justify-start"
            onClick={() => setView('report')}
          >Impact Report</Button>
          <Button
            variant={view === 'prompts' ? 'default' : 'ghost'}
            className="w-full justify-start"
            onClick={() => setView('prompts')}
          >Prompts</Button>
          <Button
            variant={view === 'versions' ? 'default' : 'ghost'}
            className="w-full justify-start"
            onClick={() => setView('versions')}
          >Versions</Button>
        </nav>
        <div className="mt-4 space-y-2">
          <span className="text-sm">Logged in as <strong>{currentUser}</strong></span>
          <Button variant="outline" size="sm" className="w-full" onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </aside>
    </div>
  );
}
